import { useEffect, useState, useCallback } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Bell, ShieldAlert, Check, Clock } from "lucide-react";
import api from "@/lib/api";

function fmt(ts) {
  try { return new Date(ts).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}

export default function AlertsBell() {
  const [data, setData] = useState({ unread: 0, alerts: [] });
  const [open, setOpen] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts");
      setData(data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchAlerts();
    const t = setInterval(fetchAlerts, 30000);
    return () => clearInterval(t);
  }, [fetchAlerts]);

  const markRead = async () => {
    try {
      await api.post("/alerts/read");
      setData((d) => ({ ...d, unread: 0 }));
    } catch {}
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) { fetchAlerts(); }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="alerts-bell-button" className="relative text-zinc-400 hover:text-white hover:bg-zinc-900">
          <Bell className="w-5 h-5" />
          {data.unread > 0 && (
            <span data-testid="alerts-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-semibold flex items-center justify-center">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 bg-[#1A1A1A] border-white/10 text-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-[#FFB020]" />
            <span className="font-display font-medium text-sm">Alertes de sécurité</span>
          </div>
          {data.unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markRead} data-testid="alerts-mark-read-button" className="h-auto py-1 px-2 text-xs text-zinc-400 hover:text-white hover:bg-zinc-800">
              <Check className="w-3 h-3 mr-1" /> Tout marquer lu
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto" data-testid="alerts-list">
          {data.alerts.length === 0 ? (
            <div className="px-4 py-10 text-center text-zinc-500 text-sm">Aucune alerte pour le moment.</div>
          ) : (
            data.alerts.map((a) => (
              <div key={a.log_id} className={`px-4 py-3 border-b border-white/5 last:border-0 ${!a.alert_read ? "bg-[#FFB020]/5" : ""}`}>
                <div className="flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 text-[#FFB020] mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">{a.user_name}</span> — {a.details_text}
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5">{a.alert_reason}</p>
                    <p className="text-xs text-zinc-600 mt-1 flex items-center gap-1 font-code"><Clock className="w-3 h-3" /> {fmt(a.timestamp)}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
